import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface KlingV25TurboParams {
  /**
   * 视频生成提示词，长度1-2500字符
   */
  prompt: string;
  /**
   * 视频时长，支持: 5（5秒）, 10（10秒）
   */
  duration: string;
  /**
   * 视频宽高比，支持: 16:9, 9:16, 1:1
   */
  aspect_ratio?: string;
  /**
   * 负向提示词
   */
  negative_prompt?: string;
  /**
   * CFG缩放比例
   */
  cfg_scale?: number;
  /**
   * 生成模式，支持: std（标准）, pro（专业）
   */
  mode?: string;
  /**
   * 相机控制参数
   */
  camera_control?: Record<string, any>;
  /**
   * 外部任务ID
   */
  external_task_id?: string;
}

export interface KlingV25TurboResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * kling_v2_5_turbo
 *
 * Kling AI V2.5 Turbo Video Generator
 * 
 * Model: kling-v2-5-turbo
 * Features: 文本生成视频 (Text-to-Video)
 * Duration: 5-10秒
 * Aspect Ratios: 16:9, 9:16, 1:1
 * Modes: std(标准), pro(专业)
 * 
 * API Documentation: https://app.klingai.com/cn/dev/document-api/apiReference/model/textToVideo
 *
 * @example
 * ```typescript
 * import { initSeacloud, klingV25Turbo } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await klingV25Turbo({
 *   // 参数见 KlingV25TurboParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 KlingV25TurboParams 接口定义
 * @returns 生成的资源列表
 */
export async function klingV25Turbo(
  params: KlingV25TurboParams,
): Promise<KlingV25TurboResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'kling_v2_5_turbo',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: KlingV25TurboResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
